import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

interface AnalyticsData {
  totalFunds: number;
  totalAUM: number;
  averageAUM: number;
  fundTypes: Array<{ type: string; count: number; percentage: number }>;
  riskDistribution: Array<{ risk: string; count: number; percentage: number }>;
  statusDistribution: Array<{ status: string; count: number; percentage: number }>;
  topProviders: Array<{ name: string; count: number; percentage: number }>;
  fundPerformance: Array<{ name: string; aum: number; risk: string; status: string }>;
}

interface FundAnalyticsProps {
  funds: any[];
  fundStructures: any[];
}

const FundAnalytics: React.FC<FundAnalyticsProps> = ({ funds, fundStructures }) => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [analysisMode, setAnalysisMode] = useState<'overview' | 'risk' | 'performance' | 'providers'>('overview');

  useEffect(() => {
    if (!funds || funds.length === 0) {
      setAnalyticsData(null);
      return;
    }

    // Calculate analytics data
    const totalFunds = funds.length;
    const totalAUM = funds.reduce((sum, fund) => sum + (fund.total_assets || 0), 0);
    const averageAUM = totalAUM / totalFunds;

    // Fund types distribution
    const fundTypeCounts = funds.reduce((acc, fund) => {
      acc[fund.fund_type] = (acc[fund.fund_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const fundTypes = Object.entries(fundTypeCounts).map(([type, count]) => ({
      type,
      count: count as number,
      percentage: ((count as number) / totalFunds) * 100,
    }));

    // Risk distribution
    const riskCounts = funds.reduce((acc, fund) => {
      acc[fund.risk_level] = (acc[fund.risk_level] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const riskDistribution = Object.entries(riskCounts).map(([risk, count]) => ({
      risk,
      count: count as number,
      percentage: ((count as number) / totalFunds) * 100,
    }));

    // Status distribution
    const statusCounts = funds.reduce((acc, fund) => {
      acc[fund.status] = (acc[fund.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const statusDistribution = Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count: count as number,
      percentage: ((count as number) / totalFunds) * 100,
    }));

    // Top service providers
    const providerCounts: Record<string, number> = {};
    fundStructures.forEach(structure => {
      structure.serviceProviders.forEach((provider: any) => {
        providerCounts[provider.name] = (providerCounts[provider.name] || 0) + 1;
      });
    });

    const topProviders = Object.entries(providerCounts)
      .map(([name, count]) => ({
        name,
        count,
        percentage: (count / fundStructures.length) * 100,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Fund performance data
    const fundPerformance = funds
      .map(fund => ({
        name: fund.name,
        aum: fund.total_assets || 0,
        risk: fund.risk_level,
        status: fund.status,
      }))
      .sort((a, b) => b.aum - a.aum);

    setAnalyticsData({
      totalFunds,
      totalAUM,
      averageAUM,
      fundTypes,
      riskDistribution,
      statusDistribution,
      topProviders,
      fundPerformance,
    });
  }, [funds, fundStructures]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getRiskColor = (risk: string) => {
    switch (risk.toLowerCase()) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  if (!analyticsData) {
    return (
      <Alert severity="info">
        No analytics data available. Please ensure funds are loaded.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Analysis Mode Selector */}
      <Box mb={3}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Analysis Mode</InputLabel>
          <Select
            value={analysisMode}
            label="Analysis Mode"
            onChange={(e) => setAnalysisMode(e.target.value as any)}
          >
            <MenuItem value="overview">Overview</MenuItem>
            <MenuItem value="risk">Risk Analysis</MenuItem>
            <MenuItem value="performance">Performance</MenuItem>
            <MenuItem value="providers">Service Providers</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {analysisMode === 'overview' && (
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Key Metrics
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Funds
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {analyticsData.totalFunds}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total AUM
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(analyticsData.totalAUM)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Average AUM
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(analyticsData.averageAUM)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Fund Types Distribution */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Fund Types
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {analyticsData.fundTypes.map((item) => (
                    <Box key={item.type}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{item.type}</Typography>
                        <Typography variant="body2">{item.count}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={item.percentage}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Risk Distribution */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Risk Distribution
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {analyticsData.riskDistribution.map((item) => (
                    <Box key={item.risk}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{item.risk}</Typography>
                        <Typography variant="body2">{item.count}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={item.percentage}
                        color={getRiskColor(item.risk) as any}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {analysisMode === 'risk' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Risk Analysis
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Fund Name</TableCell>
                        <TableCell>AUM</TableCell>
                        <TableCell>Risk Level</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.fundPerformance.map((fund) => (
                        <TableRow key={fund.name}>
                          <TableCell>{fund.name}</TableCell>
                          <TableCell>{formatCurrency(fund.aum)}</TableCell>
                          <TableCell>
                            <Chip
                              label={fund.risk}
                              size="small"
                              color={getRiskColor(fund.risk) as any}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={fund.status}
                              size="small"
                              color={getStatusColor(fund.status) as any}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {analysisMode === 'performance' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Fund Performance Ranking
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Rank</TableCell>
                        <TableCell>Fund Name</TableCell>
                        <TableCell>AUM</TableCell>
                        <TableCell>Risk Level</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.fundPerformance.map((fund, index) => (
                        <TableRow key={fund.name}>
                          <TableCell>
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{fund.name}</TableCell>
                          <TableCell>{formatCurrency(fund.aum)}</TableCell>
                          <TableCell>
                            <Chip
                              label={fund.risk}
                              size="small"
                              color={getRiskColor(fund.risk) as any}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={fund.status}
                              size="small"
                              color={getStatusColor(fund.status) as any}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {analysisMode === 'providers' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Service Providers
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider Name</TableCell>
                        <TableCell>Fund Count</TableCell>
                        <TableCell>Market Share</TableCell>
                        <TableCell>Usage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.topProviders.map((provider) => (
                        <TableRow key={provider.name}>
                          <TableCell>{provider.name}</TableCell>
                          <TableCell>{provider.count}</TableCell>
                          <TableCell>{provider.percentage.toFixed(1)}%</TableCell>
                          <TableCell>
                            <LinearProgress
                              variant="determinate"
                              value={provider.percentage}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default FundAnalytics;
